import { apiClient } from './client';

export interface PollingEntityStatus {
  entityType: string;
  lastProcessedTimestamp: string | null;
  status: 'active' | 'paused';
  /** Last time the watermark advanced (a record was processed). */
  updatedAt: string | null;
  /** Last time the poller attempted a fetch, regardless of result. */
  lastPolledAt: string | null;
  /**
   * Last time a poll completed without error. Written after the source
   * responds — when it lags behind `lastPolledAt`, the poller is running but
   * the source is rejecting every call.
   */
  lastSuccessfulPollAt: string | null;
  /** Last time a poll failed. */
  lastFailureAt: string | null;
  /** Error from the most recent failed poll. */
  lastFailureMessage: string | null;
  /** Failed polls since the last success. 0 when healthy. */
  consecutiveFailures: number;
}

export interface PollingDirectionStatus {
  active: boolean;
  entities: PollingEntityStatus[];
}

export type DataHubTableSyncStatus = 'ok' | 'stale' | 'loading' | 'error' | 'pending';

/** One lake table synced by the Data Hub (Epicor -> data lake). */
export interface DataHubTableStatus {
  table: string;
  status: DataHubTableSyncStatus;
  frequencyMinutes: number;
  rowsLoaded: number;
  rowsLastRun: number;
  lastRunAt: string | null;
  /** Last time the table was known to match Epicor. */
  upToDateAt: string | null;
  /** Last time new or changed rows were merged. */
  lastChangeAt: string | null;
  error: string | null;
}

/** The Data Hub sync. Not an integration direction, so it is reported apart. */
export interface DataHubPollingStatus {
  enabled: boolean;
  /** A sync run is in progress right now. */
  running: boolean;
  tables: DataHubTableStatus[];
}

export interface PollingStatusResponse {
  companyId: string;
  pollingEnabled: boolean;
  upstream: PollingDirectionStatus;
  downstream: PollingDirectionStatus;
  dataHub?: DataHubPollingStatus;
}

export async function getPollingStatus(): Promise<PollingStatusResponse> {
  return apiClient.get<PollingStatusResponse>('/api/polling-status');
}
